import type { StyleRule } from '@archipro-design/aria';
import { pxToRem, pxArrayToRem } from '@archipro-design/aria';
import { CLEARCUT_FILL_COLOR } from '@rocco/components/image';

/** Same neutral thumb as moodboards FollowSelectorItem for multiply/clearcut assets. */
const MULTIPLIED_THUMBNAIL_BG = CLEARCUT_FILL_COLOR;

/** Off-platform supplier stub domain reveal on row hover (search + recently tagged). */
export const OFF_PLATFORM_STUB_DOMAIN_HOVER_MS = 200;

export const ResultTile: StyleRule = () => ({
    height: pxToRem(66),
    width: '100%',
    padding: pxArrayToRem([9, 8, 9, 16]),
    cursor: 'pointer',
    [`&:hover`]: {
        background: `rgba(0, 0, 0, 0.06)`,
    },
    [`&:hover [data-off-platform-stub-domain]`]: {
        maxHeight: pxToRem(16),
        opacity: 1,
        transform: 'translateY(0)',
        transitionProperty: 'opacity, transform, max-height',
        transitionDuration: `${OFF_PLATFORM_STUB_DOMAIN_HOVER_MS}ms`,
        transitionTimingFunction: 'ease-out',
    },
    [`&:hover [data-off-platform-stub-title]`]: {
        transform: `translateY(${pxToRem(-1)})`,
        transitionProperty: 'transform',
        transitionDuration: `${OFF_PLATFORM_STUB_DOMAIN_HOVER_MS}ms`,
        transitionTimingFunction: 'ease-out',
    },
});

/** Off-platform stub avatars stay transparent so the frosted modal shows through the outline ring. */
export const ResultTileThumb: StyleRule<{
    logoBackground?: string;
    isProfessional?: boolean;
    isOffPlatformStub?: boolean;
    /** When true, thumb uses MULTIPLIED_THUMBNAIL_BG unless logoBackground is set. */
    usesNeutralMultiply?: boolean;
    /** When true, shows a default grey border (logo bg too close to white or absent). */
    useDefaultBorder?: boolean;
}> = ({
    theme,
    logoBackground,
    isProfessional,
    isOffPlatformStub,
    usesNeutralMultiply,
    useDefaultBorder,
}) => {
    const thumbBackground = isOffPlatformStub
        ? 'transparent'
        : logoBackground ||
          (usesNeutralMultiply
              ? MULTIPLIED_THUMBNAIL_BG
              : theme.siteVariables.colors.primitive.white);

    const baseStyles = {
        background: thumbBackground,
        position: 'relative' as const,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: pxToRem(48),
        width: pxToRem(48),
        minWidth: pxToRem(48),
        ...(usesNeutralMultiply ? { isolation: 'isolate' as const } : {}),
    };

    if (isProfessional) {
        return {
            ...baseStyles,
            borderRadius: '50%',
            ...(useDefaultBorder
                ? {
                      border: `1px solid ${theme.siteVariables.colors.white[100]}`,
                  }
                : {}),
            padding: pxToRem(4),
            overflow: 'hidden',
        };
    }

    return {
        ...baseStyles,
        borderRadius: pxToRem(8),
        overflow: 'hidden',
    };
};

export const ResultTileContent: StyleRule = () => ({
    justifyContent: 'center',
    maxWidth: pxToRem(282),
});

export const ResultTileTitle: StyleRule = ({ theme }) => ({
    color: theme.siteVariables.colors.charcoal['300'],
    fontWeight: theme.siteVariables.textStyles.Text.Medium65.fontWeight,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

/** Title + hidden domain stack for off-platform supplier stubs (domain reveals on row hover). */
export const OffPlatformStubTextStack: StyleRule = () => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 0,
    minWidth: 0,
    width: '100%',
});

export const OffPlatformStubDomain: StyleRule = ({ theme }) => ({
    color: theme.siteVariables.colors.charcoal[100],
    lineHeight: 1.15,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxHeight: 0,
    opacity: 0,
    marginTop: 0,
    transform: `translateY(${pxToRem(6)})`,
    transition: 'none',
});

export const OffPlatformStubTitle: StyleRule = ({ theme }) => ({
    color: theme.siteVariables.colors.charcoal['300'],
    fontWeight: theme.siteVariables.textStyles.Text.Medium65.fontWeight,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    transition: 'none',
});
export const ResultTileProfessional: StyleRule = ({ theme }) => ({
    color: theme.siteVariables.colors.charcoal[100],
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginTop: pxToRem(2),
});

/** Centres {@see archipro-symbol-logo.svg} in off-platform supplier stub tiles. */
export const OffPlatformStubLogoSlot: StyleRule = () => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    padding: pxToRem(6),
    color: '#171616',
    pointerEvents: 'none',
    '& svg': {
        width: '100%',
        height: '100%',
    },
    '& path': {
        fill: 'currentColor',
    },
});
